import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Phone, Check, ArrowRight } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/site/motion";
import GreenShieldForm from "@/components/site/GreenShieldForm";
import InsuranceProviders from "@/components/sections/InsuranceProviders";
import { CONTACT } from "@/data/content";
import useSEO from "@/hooks/useSEO";

const steps = [
  { t: "Share your details", d: "Tell us your GreenShield plan information through our secure Verify My Benefits form." },
  { t: "We verify your coverage", d: "As an approved GreenShield provider, our admissions team confirms your eligibility directly." },
  { t: "Begin treatment", d: "You may qualify for coverage of your residential treatment — often with same-day admission." },
];

const GreenShieldPage = () => {
  const navigate = useNavigate();
  useSEO({
    title: "GreenShield Rehab Coverage in Toronto — Direct-Billed Inpatient Treatment",
    description:
      "Approved GreenShield provider. Inpatient drug & alcohol rehab in Toronto, direct-billed to GreenShield Canada. Same-day admission for eligible plans. Verify your coverage in 60 seconds.",
    keywords: "GreenShield rehab, GreenShield addiction treatment Toronto, GreenShield inpatient coverage, drug rehab covered by GreenShield Canada",
    canonical: "https://newbeginningsrecovery.ca/greenshield",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);
  return (
    <div data-testid="greenshield-page" className="bg-sand min-h-screen">
      <Navbar />

      <section className="pt-40 md:pt-48 pb-20 md:pb-24">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 rounded-full bg-sage/20 text-forest px-4 py-1.5 text-sm font-semibold mb-6">
              <ShieldCheck className="w-4 h-4" /> Approved GreenShield Provider
            </span>
            <h1 className="font-serif text-5xl md:text-7xl text-forest tracking-tight leading-[0.98]">
              GreenShield <span className="italic text-terracotta">Coverage</span>
            </h1>
            <p className="mt-6 text-lg text-ink2 leading-relaxed max-w-xl">
              New Beginnings Recovery Home is an approved GreenShield provider. If you have GreenShield benefits,
              our admissions team can help verify your coverage before admission. You may qualify for coverage of
              your residential treatment. Contact us today to verify your benefits.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button onClick={() => navigate("/verify-benefits")} data-testid="greenshield-verify" className="h-13 px-7 py-6 rounded-full bg-terracotta hover:bg-[#b45a3d] text-white font-semibold">
                Verify My Benefits <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              <a href={CONTACT.phoneHref} className="h-auto px-7 py-4 rounded-full border border-forest text-forest hover:bg-forest hover:text-sand flex items-center gap-2 font-semibold transition-colors">
                <Phone className="w-5 h-5" /> {CONTACT.phone}
              </a>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <img src="https://images.unsplash.com/photo-1551601651-2a8555f1a136?auto=compress&w=1200&q=80" alt="Verifying GreenShield insurance coverage for addiction treatment" className="w-full h-[360px] md:h-[500px] object-cover arch-frame" />
          </Reveal>
        </div>
      </section>

      <section className="bg-sand2 py-20 md:py-28">
        <div className="mx-auto max-w-[1100px] px-5 md:px-10">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl text-forest tracking-tight mb-12 text-center">How coverage verification works</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.1}>
                <div className="h-full rounded-3xl bg-white border border-line p-8">
                  <span className="grid place-items-center w-11 h-11 rounded-full bg-forest text-sand font-serif text-lg mb-5">{i + 1}</span>
                  <h3 className="font-serif text-2xl text-forest">{s.t}</h3>
                  <p className="mt-3 text-ink2 leading-relaxed">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-sand">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-2 gap-14 items-start">
          <Reveal>
            <p className="text-sm uppercase tracking-[0.25em] text-terracotta mb-4">GreenShield Clients</p>
            <h2 className="font-serif text-4xl md:text-5xl text-forest tracking-tight leading-[1.02]">GreenShield coverage for addiction treatment</h2>
            <p className="mt-5 text-lg text-ink2 leading-relaxed">
              As an approved GreenShield provider in Toronto, New Beginnings Recovery Home helps GreenShield clients
              access residential drug and alcohol treatment. Many GreenShield benefit plans include coverage for
              addiction and mental health treatment — and our admissions team will verify your specific GreenShield
              coverage before you arrive, so you know exactly where you stand.
            </p>
            <p className="mt-4 text-lg text-ink2 leading-relaxed">
              Whether you're a GreenShield member or a private-pay client, we make the intake process simple,
              confidential and human — with immediate admittance and shorter wait lists.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <GreenShieldForm />
          </Reveal>
        </div>
      </section>

      <InsuranceProviders compact />

      <section className="py-20 md:py-28">
        <div className="mx-auto max-w-[900px] px-5 md:px-10">
          <div className="rounded-3xl bg-forest text-sand p-8 md:p-12 text-center">
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.02]">Have GreenShield benefits?</h2>
            <p className="mt-4 text-sand/75 max-w-xl mx-auto">Let our admissions team confirm your coverage today — confidentially and with no obligation.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button onClick={() => navigate("/verify-benefits")} className="h-13 px-7 py-6 rounded-full bg-terracotta hover:bg-[#b45a3d] text-white font-semibold">Verify My Benefits</Button>
              <a href={CONTACT.phoneHref} className="h-auto px-7 py-4 rounded-full border border-sand/40 text-sand hover:bg-sand hover:text-forest flex items-center justify-center gap-2 font-semibold transition-colors">
                <Phone className="w-5 h-5" /> {CONTACT.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default GreenShieldPage;
